//#region Libraries
import {
    useRef, 
    useState
} from "react";
//#endregion
//#region Styles
import {
    Container, File, 
    Content,
    BeforeUpload, 
    Detail 
} from './styles';
//#endregion
//#region Components
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
//#endregion
//#region Icons
import DownloadIcon from "icons/DownloadIcon";
import FileIcon from "icons/FileIcon";
import TrashIcon from "icons/TrashIcon";
//#endregion
//#region Utils
import {
    getMBFromBytes, 
    fileToBase64, 
    getfileTypes,
    isValidFileType, 
    isEmptyFile,
    isValidByteSize} from "./utils/helpers";
import { MEGABYTE_IN_BYTES } from "./utils/constants";
//#endregion

const CustomFileInput = ({
    fileTypes = ["csv", "xlsx"], 
    file, 
    maxSizeMB = 30,
    onChange, error, errorText
}) => {
    //#region Refs
    const $input = useRef();
    const $container = useRef();
    //#endregion
    //#region States
    const [errorMessage,  setErrorMessage] = useState(null);
    const [className, setClassName] = useState("");
    //#endregion
    //#region Effects
    //#endregion
    //#region Functions
    const showError = (message) => {
        setErrorMessage(message)
        clearFile();
    }
    const loadFile = async (newFile) => {
        setErrorMessage(null);
        if (!isValidFileType(fileTypes, newFile.name)) {
            showError(`¡Ups! Tu archivo no es compatible con ${getfileTypes(fileTypes)}. Vuelve a intentarlo.`);
            return;
        }
        if (isEmptyFile(newFile.size)) {
            showError("!Ups! Tu archivo está vacío. Revísalo y vuelve a intentarlo.");
            return;
        }
        if (!isValidByteSize(newFile.size, maxSizeMB*MEGABYTE_IN_BYTES)) {
            showError(`¡Ups! Tu archivo excedió los ${maxSizeMB} MB de peso. Revísalo y vuelve a intentarlo.`);
            return;
        }
        const base64 = await fileToBase64(newFile);
        onChange({
            name: newFile.name,
            size: newFile.size,
            base64
        });
    }
    const clearFile = () => {
        onChange({
            name: "",
            size: 0, 
            base64: ""
        });
        $container.current.reset();
    }
    //#region Drag and drop file
    const inInputFile = (e) => {
        const contentRect = $container.current.children[0].getBoundingClientRect();
        return (
            e.clientX >= contentRect.x && 
            e.clientX <= contentRect.x + contentRect.width && 
            e.clientY >= contentRect.y && 
            e.clientY <= contentRect.y + contentRect.height
        );
    }
    const removeClassName = value => {
        if (className.includes(value))
            setClassName(className.replaceAll(value, "").trim()); 
    }
    const addClassName = value => {
        if (!className.includes(value))
            setClassName(`${className} ${value}`.trim());
    }
    const getSizeText = () => {
        if (file.size >= 1000)
            return `${getMBFromBytes(file.size)}MB`;
        return `${file.size}B`;
    }
    const onDragOver = (e) => {
        e.stopPropagation();
        e.preventDefault();
    }
    const onDragLeave = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!inInputFile(e) && !thereIsFile()) 
            removeClassName("drag-enter");
    }
    const onDragEnter = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (inInputFile(e) && !thereIsFile()) 
            addClassName("drag-enter");
    }
    const thereIsFile = () => file.base64 || file.name;
    const onDropFile = (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeClassName("drag-enter");
        const [newFile] = e.dataTransfer.files;
        if (newFile && !thereIsFile())
            loadFile(newFile);
    }
    //#endregion
    //#endregion
    return (
        <Container ref={$container}>
            <Content
                onDragOver={onDragOver}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDrop={onDropFile}
                className={[className, error && "error"].join(" ")}>
                <h3>
                    {file.base64 
                    ? "Hemos subido tu archivo" 
                    : "Máximo 1 archivo"}
                </h3>
                {thereIsFile()
                    ? <File>
                        <div>
                            <span data-role="icon">
                                <FileIcon/>
                            </span>
                            <span>{file.name}</span>
                            {file.size > 0
                                && <strong>{getSizeText()}</strong>}
                        </div>
                        <span data-role="icon" onClick={clearFile}>
                            <TrashIcon/>
                        </span>
                    </File>
                    : <BeforeUpload onClick={() => $input.current.click()}>
                        <DownloadIcon/>
                        <h5>Subir archivo</h5>
                    </BeforeUpload>}
                <Detail>
                    Archivos permitidos {getfileTypes(fileTypes)}<br/>
                    (Tamaño máximo por cada archivo {maxSizeMB} MB)
                </Detail>
            </Content>
            <ErrorMessage
                error={errorMessage || error}
                text={errorMessage || errorText}/>
            <input ref={$input}
                type="file"
                onChange={() => {
                    const [newFile] = $input.current.files;
                    loadFile(newFile);
                }}
                accept={getfileTypes(fileTypes)}
                hidden/>
        </Container>
    );
}

export default CustomFileInput;